import React from 'react';
import { Card, CardContent } from './ui/card';
import { TrendingUp, TrendingDown } from 'lucide-react';

const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'blue', large = false, staggerIndex = 0 }) => {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700'
  };
  
  const iconColorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    amber: 'bg-amber-100 text-amber-600',
    red: 'bg-red-100 text-red-600',
    purple: 'bg-purple-100 text-purple-600',
    indigo: 'bg-indigo-100 text-indigo-600'
  };

  return (
    <Card className={`kpi-rise kpi-rise-${Math.min(staggerIndex, 7)} ${colorClasses[color]} border transition-all hover:shadow-md`}>
      <CardContent className={`${large ? 'p-6' : 'p-4'}`}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium opacity-80">{title}</p>
            <p className={`${large ? 'text-4xl' : 'text-2xl'} font-bold mt-1`}>{value}</p>
            {subtitle && <p className="text-xs opacity-70 mt-1">{subtitle}</p>}
            {trend !== undefined && (
              <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {trend >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                <span>{trend >= 0 ? '+' : ''}{trendValue || trend}</span>
              </div>
            )}
          </div>
          {Icon && (
            <div className={`p-3 rounded-xl ${iconColorClasses[color]}`}>
              <Icon className={`${large ? 'h-6 w-6' : 'h-5 w-5'}`} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Mini graphique en barres
const MiniBarChart = ({ data, color = 'blue' }) => {
  if (!data || data.length === 0) return null;
  const maxValue = Math.max(...data.map(d => d.value), 1);
  
  const colorClass = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    amber: 'bg-amber-500',
    purple: 'bg-purple-500'
  }[color] || 'bg-blue-500';
  
  return (
    <div className="flex items-end gap-1 h-16">
      {data.map((item, idx) => (
        <div key={idx} className="flex-1 flex flex-col items-center gap-1">
          <div 
            className={`w-full rounded-t ${colorClass} transition-all duration-300`}
            style={{ height: `${(item.value / maxValue) * 100}%`, minHeight: item.value > 0 ? '4px' : '0' }}
          />
          <span className="text-[10px] text-gray-400">{item.label}</span>
        </div>
      ))}
    </div>
  );
};

// Composant de classement COOPER
const CooperRanking = ({ rankings }) => {
  if (!rankings || rankings.length === 0) return null;
  
  return (
    <div className="space-y-3">
      {rankings.slice(0, 5).map((cooper, idx) => (
        <div key={cooper.cooper_id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
            idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-amber-600' : 'bg-gray-300'
          }`}>
            {idx + 1}
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900">{cooper.nom || 'COOPER'}</p>
            <p className="text-xs text-gray-500">{cooper.membres?.total || 0} membres</p>
          </div>
          <div className="text-right">
            <p className="font-bold text-green-600">{(cooper.ca?.total_ttc || 0).toLocaleString('fr-FR')}€</p>
            <p className="text-xs text-gray-500">+{(cooper.ca?.mois_ttc || 0).toLocaleString('fr-FR')}€ ce mois</p>
          </div>
        </div>
      ))}
    </div>
  );
};

export { StatCard, MiniBarChart, CooperRanking };
